import { useEffect, Fragment, ReactNode, FC } from 'react';
import styled from 'styled-components';
import closeIcon from '../../assets/icons/play-together/close.svg';
import { createPortal } from 'react-dom';

export interface IDialog {
  children: ReactNode;
  closeCallback: () => void;
  isOpen: boolean;
  noPadding?: boolean;
  noStyles?: boolean;
  canClose?: boolean;
  className?: string;
}

const ModalContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
`;

const DialogWrapper = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
    border-radius: 15px;
    color: ${color.grayscale[0]};
    text-align: center;
    background: ${color.grayscale[900]};
    font-size: 14px;
    z-index: 11;
    position: fixed;
    left: 50%;
    top: 50%;
    box-shadow: ${shadow.regular};
    transform: translate(-50%, -50%);
    text-align: center;
    min-width: 384px;
    max-width: 75vw;
    max-height: 90vh;
    overflow-y: auto;

    @media (max-width: 1500px) {
      width: 99%;
      max-width: 99Vw;
    } 
  `;
});

const DialogContent = styled.div`
  position: relative;
  padding: 50px;

  &.no_padding {
    padding: 0;
  }
`;

const Background = styled.div`
  z-index: 990;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: fixed;
  width: 24px;
  height: 24px;
  top: 10px;
  right: 10px;
  border: 0;
  background-color: transparent;
  padding: 0;
`;

const CloseIcon = styled.img`
  display: block;
  height: 24px;
  width: 24px;
`;

const Dialog: FC<IDialog> = ({ children, closeCallback, isOpen, noPadding, canClose, noStyles, className }) => {
  const handleEscapePress = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closeCallback();
    }
  };

  let contentClass = '';

  if (noPadding) {
    contentClass = 'no_padding';
  }

  useEffect(() => {
    document.addEventListener('keyup', handleEscapePress);

    return () => {
      document.removeEventListener('keyup', handleEscapePress);
    };
  });

  const content = (
    <DialogContent className={contentClass}>
      {canClose && (
        <CloseButton onClick={closeCallback} title="Close Dialog">
          <CloseIcon src={closeIcon} alt="" />
        </CloseButton>
      )}

      {children}
    </DialogContent>
  );

  if (isOpen) {
    return createPortal(
      <ModalContainer>
        {noStyles ? (
          <div className={className}>{content}</div>
        ) : (
          <DialogWrapper className={className}>{content}</DialogWrapper>
        )}

        <Background onClick={closeCallback}></Background>
      </ModalContainer>,
      document.body
    );
  }
  return <Fragment />;
};

export default Dialog;
